import { requisicaoAutenticada } from './http';
import type { DashboardIcms } from './perfil';

/**
 * Dados do painel — os três dashboards de ICMS.
 *
 * ```
 * GET /api/icms/dashboard/clientes/:clienteId/:ano      → DashboardCliente
 * GET /api/icms/dashboard/assessor/:assessorId/:ano     → DashboardParticipante
 * GET /api/icms/dashboard/fornecedor/:fornecedorId/:ano → DashboardParticipante
 * ```
 *
 * Estes tipos espelham `DashboardCliente` e `DashboardParticipante` de
 * `apps/api/src/modules/icms/icms-dashboard.service.ts`, campo a campo. Toda a
 * agregação é de `@deca/domain/relatorios` (RN-150 a RN-176): nem o backend nem
 * a tela fazem conta fiscal (RN-615).
 *
 * ## Qual dos três consultar
 *
 * Quem responde é `GET /api/icms/perfil/:businessId` (ver `servicos/perfil.ts`).
 * Até ele existir, esta tela rodava com uma **amostra** marcada como
 * demonstração — o oposto do legado, em que três widgets do pré-registro eram
 * cópia literal do template Fuse, com "R$ 100.000,00" e "50% de deságio"
 * renderizados sem nenhuma chamada de API e sem nada dizendo que eram exemplo
 * (dívida #49, RN-634). A amostra saiu junto com a lacuna que a justificava.
 *
 * A regra continua valendo: **se o dado não existe, a UI diz que não existe.**
 * O que não existe vira estado vazio ou travessão, nunca zero.
 */

/**
 * Espelha `MesDoDashboard` da API.
 *
 * Os três valores de dinheiro têm significados que não se trocam (RN-010,
 * RN-065): `valorUtilizacao` é o crédito transferido, `beneficioEconomico` é o
 * que o cliente **deixa de pagar** e `valorPago` é o desembolso dele. Só o
 * benefício leva o verde de economia.
 */
export interface MesDoPainel {
  /** `AAAA-MM`. Sempre os 12 meses do ano fiscal (RN-167). */
  readonly competencia: string;
  readonly valorUtilizacao: number;
  readonly beneficioEconomico: number;
  readonly valorPago: number;
  /** Escala 0–100, nunca fração (RN-078). */
  readonly desagioVendaPercentual: number | null;
  readonly fornecedorId: string | null;
  readonly fornecedorNome: string | null;
  /** RN-151 — status do contrato **daquele** fornecedor, não do ano. */
  readonly statusContrato: string | null;
  /** RN-150 — `SPED_MATERIALIZADO`, `CONTRATO` ou `INDEFINIDO`. */
  readonly origemDesagio: string;
}

/** Espelha `DashboardCliente` da API — uma série de 12 meses. */
export interface PainelDoCliente {
  readonly clienteId: string;
  /** Ano da operação. */
  readonly ano: number;
  /** Ano fiscal dos SPEDs que alimentam o relatório (`ano − 1`). */
  readonly anoSped: number;
  readonly meses: readonly MesDoPainel[];
  readonly totais: {
    readonly valorUtilizacao: number;
    readonly beneficioEconomico: number;
    readonly valorPago: number;
    /**
     * RN-175 — **não use sem consultar `metricasIndisponiveis`.** Enquanto a
     * métrica estiver declarada indisponível, este número é ausência de dado,
     * não apuração (D-008).
     */
    readonly mesesAproveitamento: number;
  };
  readonly metricasIndisponiveis: readonly string[];
}

/** Espelha `LinhaParticipante` — uma linha cliente × fornecedor. */
export interface LinhaDoParticipante {
  readonly clienteId: string;
  readonly clienteNome: string | null;
  readonly fornecedorId: string;
  readonly fornecedorNome: string | null;
  readonly status: string;
  /** RN-169 — a linha veio de projeção do ano-alvo, não de contrato real. */
  readonly simulado: boolean;
  readonly valorUtilizacao: number;
  readonly beneficioEconomico: number;
  /** Spread do assessor (RN-170) ou receita do fornecedor (RN-173). */
  readonly comissao: number;
}

/**
 * Espelha `DashboardParticipante` — assessor e fornecedor.
 *
 * Formato diferente do painel do cliente de propósito: aqui não há série de
 * meses, e sim uma linha por par cliente × fornecedor, mais um bloco `geral`.
 */
export interface PainelDoParticipante {
  readonly businessId: string;
  readonly ano: number;
  readonly anoSped: number;
  /** RN-169 — o ano é projeção, não realizado. Só o assessor projeta. */
  readonly projetado: boolean;
  readonly linhas: readonly LinhaDoParticipante[];
  readonly geral: {
    readonly totalCalculos: number;
    readonly totalContratosFechados: number;
    /** RN-171 — escala 0–100, nunca `NaN` (dívida #34). Este **é** percentual. */
    readonly percentualConversaoPercentual: number;
    readonly valorUtilizacaoTotal: number;
    readonly beneficioEconomicoTotal: number;
    readonly comissaoTotal: number;
    /**
     * RN-172 / RN-174 — média por contrato fechado, **em reais**.
     *
     * O nome legado (`mediaDesagioVendido`) engana: não é percentual de
     * deságio, é `soma(comissão dos meses fechados) / contratos fechados`. O
     * sufixo `Reais` existe para a tela usar `ValorFiscal` e **não** anexar um
     * `%` — que é a forma exata do RN-078 (o mesmo `number` lido em duas
     * escalas) reaparecendo por outro caminho.
     */
    readonly mediaPorContratoFechadoReais: number;
    /** RN-175 — mesma ressalva de `PainelDoCliente.totais`. */
    readonly mesesAproveitamento: number;
  };
  readonly metricasIndisponiveis: readonly string[];
}

/**
 * A API declara, em texto, quais métricas ela não consegue calcular — em vez de
 * mandar `0`, que era o que o legado fazia em `mediaDesagioVendido` e
 * `mesesAproveitamento` (RN-172, dívida #46) e que se lê como valor apurado.
 *
 * Cada item vem como `"<campo> (<RN>) — <motivo>"`. Esta função devolve o texto
 * inteiro do item que fala do campo pedido, ou `null` se a métrica está de pé.
 */
export function motivoDeIndisponibilidade(
  metricasIndisponiveis: readonly string[],
  campo: string,
): string | null {
  return metricasIndisponiveis.find((item) => item.startsWith(campo)) ?? null;
}

/** RN-135 — o `:id` da rota é sempre a empresa corrente, revalidada no servidor. */
export async function carregarPainelDoCliente(
  clienteId: string,
  ano: number,
  sinal?: AbortSignal,
): Promise<PainelDoCliente> {
  return requisicaoAutenticada<PainelDoCliente>(
    `/icms/dashboard/clientes/${encodeURIComponent(clienteId)}/${ano}`,
    { sinal },
  );
}

/** Assessor e fornecedor compartilham o formato de resposta, não a rota. */
const CAMINHO_DO_PARTICIPANTE: Readonly<Record<'ASSESSOR' | 'FORNECEDOR', string>> = {
  ASSESSOR: 'assessor',
  FORNECEDOR: 'fornecedor',
};

export async function carregarPainelDoParticipante(
  dashboard: Exclude<DashboardIcms, 'CLIENTE'>,
  businessId: string,
  ano: number,
  sinal?: AbortSignal,
): Promise<PainelDoParticipante> {
  const caminho = CAMINHO_DO_PARTICIPANTE[dashboard];
  return requisicaoAutenticada<PainelDoParticipante>(
    `/icms/dashboard/${caminho}/${encodeURIComponent(businessId)}/${ano}`,
    { sinal },
  );
}
